import copy from '../copy/en/intake.json' with { type: 'json' };

const profile = (key: string): string => `intakeProfile.${key}`;

/** Explicit convenience copies; source edits never establish a live link. */
export function bindNativeCopyActions(form: HTMLFormElement): { sync(): void; dispose(): void } {
  const input = (name: string): HTMLInputElement => form.elements.namedItem(name) as HTMLInputElement;
  const doctor = [profile('people.doctorName'), profile('people.contactEmail')];
  const contact = [profile('people.mainContactName'), profile('people.mainContactEmail')];
  const actions: { button: HTMLButtonElement; sources: HTMLInputElement[]; targets: HTMLInputElement[]; click(): void }[] = [];
  const containers: HTMLElement[] = [];

  function action(label: string, accessibleLabel: string, from: string[], to: string[], host: HTMLElement): void {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'native-copy-action';
    button.textContent = label;
    button.setAttribute('aria-label', accessibleLabel);
    const sources = from.map(input);
    const targets = to.map(input);
    button.setAttribute('aria-controls', targets.map(target => target.id).join(' '));
    const enabled = (): boolean => sources.every(source => source.value.trim() && !source.matches(':disabled'))
      && targets.every(target => !target.matches(':disabled') && !target.readOnly);
    const click = (): void => {
      if (!enabled() || button.matches(':disabled')) return;
      // Set the complete pair before any listener collects or saves the form.
      const values = sources.map(source => source.value.trim());
      targets.forEach((target, index) => { target.value = values[index]!; });
      for (const target of targets) {
        target.dispatchEvent(new Event('input', { bubbles: true }));
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
      targets[0]!.focus();
      sync();
    };
    button.addEventListener('click', click);
    actions.push({ button, sources, targets, click });
    host.append(button);
  }

  const legal = input(profile('business.legalName'));
  const legalHost = document.createElement('div');
  legalHost.className = 'native-copy-input';
  legal.before(legalHost);
  legalHost.append(legal);
  containers.push(legalHost);
  action(copy.sameAsPracticeName, copy.copyPracticeNameToLegalBusinessName, ['practiceName'], [legal.name], legalHost);

  function sectionActions(target: string): HTMLElement {
    const host = document.createElement('div');
    host.className = 'native-copy-actions';
    const card = input(target).closest('fieldset')!;
    card.insertBefore(host, card.querySelector('.field-stack'));
    containers.push(host);
    return host;
  }
  action(copy.useDoctorDetails, copy.copyDoctorToMainContact, doctor, contact, sectionActions(contact[0]!));
  const signerHost = sectionActions('signerName');
  action(copy.useDoctorDetails, copy.copyDoctorToSigner, doctor, ['signerName', 'email'], signerHost);
  action(copy.useMainContact, copy.copyMainContactToSigner, contact, ['signerName', 'email'], signerHost);

  function sync(): void {
    for (const { button, sources, targets } of actions) {
      const disabled = sources.some(source => !source.value.trim() || source.matches(':disabled'))
        || targets.some(target => target.matches(':disabled') || target.readOnly);
      if (button.disabled !== disabled) button.disabled = disabled;
    }
  }
  const reset = (): void => { queueMicrotask(sync); };
  form.addEventListener('input', sync);
  form.addEventListener('change', sync);
  form.addEventListener('reset', reset);
  const observer = new MutationObserver(sync);
  observer.observe(form, { attributes: true, subtree: true, attributeFilter: ['disabled', 'readonly'] });
  sync();
  return {
    sync,
    dispose(): void {
      observer.disconnect();
      form.removeEventListener('input', sync);
      form.removeEventListener('change', sync);
      form.removeEventListener('reset', reset);
      actions.forEach(({ button, click }) => { button.removeEventListener('click', click); button.remove(); });
      legalHost.before(legal);
      containers.forEach(container => container.remove());
    },
  };
}
